import type { Variants } from 'framer-motion'

// Shared easing curve - smooth ease-out
export const ease = [0.25, 0.46, 0.45, 0.94] as const

// Reduced motion fallback
export const reducedMotionVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
}

// ============================================
// SECTION-LEVEL VARIANTS (orchestration only)
// ============================================

/** Section container - orchestrates staggered children */
export const sectionVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
}

// ============================================
// CONTENT BLOCK VARIANTS (headers, paragraphs)
// ============================================

/** Header/title - subtle upward reveal */
export const headerVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease },
  },
}

/** Large cards or feature blocks - deeper reveal */
export const cardVariants: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease },
  },
}

/** Grid items - shorter distance, quicker */
export const gridItemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease },
  },
}

/** List items - subtle stagger */
export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease },
  },
}

// ============================================
// SPLIT LAYOUT VARIANTS (converging animations)
// ============================================

/** Content sliding from left (for content-left layouts) */
export const slideFromLeftVariants: Variants = {
  hidden: { opacity: 0, x: -32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease },
  },
}

/** Content sliding from right (for content-right layouts) */
export const slideFromRightVariants: Variants = {
  hidden: { opacity: 0, x: 32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease },
  },
}

// ============================================
// HERO-SPECIFIC VARIANTS (above-fold, load animations)
// ============================================

/** Hero badge/logo - small fade */
export const heroBadgeVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease },
  },
}

/** Hero headline - prominent reveal */
export const heroHeadlineVariants: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease },
  },
}

/** Hero description - follows headline */
export const heroDescriptionVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease },
  },
}

/** Hero buttons container */
export const heroButtonsVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease },
  },
}

/** Hero image/media - dramatic reveal */
export const heroMediaVariants: Variants = {
  hidden: { opacity: 0, y: 40, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease },
  },
}

// ============================================
// GRID ORCHESTRATION
// ============================================

/** Grid container with configurable stagger */
export const gridContainerVariants = (
  staggerDelay: number = 0.1,
  childDelay: number = 0.2
): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelay,
      delayChildren: childDelay,
    },
  },
})

// Re-export framer-motion hooks for convenience
export { useInView, useReducedMotion } from 'framer-motion'
